import { useEffect, useState, useCallback } from "react";
import { useLocation } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import { BookOpen, X, ChevronDown, Loader2, ExternalLink } from "lucide-react";
import { cn } from "../lib/utils";
import { useAuth } from "../context/AuthContext";

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * HelpWidget — session 416
 *
 * Floating 📖 icon in the bottom-right of every screen. Click → a side
 * panel slides in from the right with the manual chapter most relevant
 * to the current route. The chapter picker at the top lets you jump to
 * any other chapter without leaving the page.
 *
 * Route-to-chapter mapping is server-driven (see
 * backend/routers/manual.py::CHAPTER_ROUTE_MAP) so re-organising URLs
 * doesn't break the widget.
 *
 * Hidden on login/logout pages so the pre-auth flow stays clean.
 * PCWs don't see the widget — they use the mobile handbook.
 */
export default function HelpWidget() {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [toc, setToc] = useState([]);
  const [chapter, setChapter] = useState(null);
  const [loading, setLoading] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);

  // Don't show widget on unauthenticated routes or for PCWs.
  const { user: authUser } = useAuth();
  const currentUser = authUser || (() => {
    try { return JSON.parse(localStorage.getItem("user") || "{}"); } catch { return {}; }
  })();
  const isPcw = currentUser?.role === "pcw";
  const isPublicRoute = ["/login", "/logout", "/register", "/reset-password", "/sign", "/family"]
    .some((p) => location.pathname.startsWith(p));

  const authHeaders = () => {
    const t = localStorage.getItem("token");
    return t ? { Authorization: `Bearer ${t}` } : {};
  };

  const loadToc = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/manual/toc`, { headers: authHeaders() });
      if (res.ok) {
        const data = await res.json();
        setToc(data.items || []);
      }
    } catch { /* silent — offline is fine */ }
  }, []);

  const loadChapter = useCallback(async (chapterId, route) => {
    setLoading(true);
    try {
      const url = chapterId === "auto"
        ? `${API}/api/manual/chapter/auto?route=${encodeURIComponent(route || location.pathname)}`
        : `${API}/api/manual/chapter/${encodeURIComponent(chapterId)}`;
      const res = await fetch(url, { headers: authHeaders() });
      if (res.ok) {
        const data = await res.json();
        setChapter(data);
      } else {
        setChapter({ id: chapterId, title: "Chapter not found", body: "That section of the manual isn't available yet." });
      }
    } catch (e) {
      setChapter({ id: chapterId, title: "Manual unavailable", body: "Couldn't load the manual. Please check your connection and try again." });
    } finally {
      setLoading(false);
    }
  }, [location.pathname]);

  // Whenever the panel opens, load TOC + the auto-picked chapter for
  // the current route. Also refresh when the route changes while the
  // panel is open, so navigating around updates the context.
  useEffect(() => {
    if (!open) return;
    loadToc();
    loadChapter("auto", location.pathname);
  }, [open, location.pathname, loadToc, loadChapter]);

  // Expose an open handle on the window so other components (namely the
  // consolidated "?" FAB in GlobalQuickActions) can trigger the manual
  // panel without needing its own floating icon. Removed the widget's
  // own trigger button below to declutter the bottom-right corner.
  useEffect(() => {
    window.__avidaOpenManual = () => setOpen(true);
    return () => { if (window.__avidaOpenManual) delete window.__avidaOpenManual; };
  }, []);

  if (isPcw || isPublicRoute) return null;

  return (
    <>
      {/* Floating trigger removed — the "?" FAB in GlobalQuickActions
          now opens the manual through window.__avidaOpenManual(). Keeping
          the slide-in panel + all chapter/TOC logic below unchanged. */}

      {/* Slide-in panel */}
      {open && (
        <div className="fixed inset-y-0 right-0 z-[9995] w-full sm:w-[440px] flex flex-col shadow-2xl bg-white animate-in slide-in-from-right duration-200"
             data-testid="help-widget-panel">
          {/* Header */}
          <div className="px-5 py-3.5 flex items-center gap-3 border-b border-slate-100" style={{ background: "#003366" }}>
            <BookOpen className="w-4 h-4 shrink-0" style={{ color: "#FFDA00" }} />
            <div className="flex-1 min-w-0">
              <p className="text-[9px] font-bold uppercase tracking-widest text-slate-300">Avida user manual · v1.1</p>
              {/* Chapter picker */}
              <div className="relative mt-0.5">
                <button
                  type="button"
                  onClick={() => setPickerOpen((v) => !v)}
                  className="flex items-center gap-1 text-white font-bold text-sm hover:text-yellow-200 group"
                  data-testid="help-widget-picker-toggle"
                >
                  <span className="truncate max-w-[280px]">{chapter?.title || "Loading…"}</span>
                  <ChevronDown className={cn("w-3.5 h-3.5 shrink-0 transition-transform", pickerOpen && "rotate-180")} />
                </button>
                {pickerOpen && (
                  <div className="absolute left-0 top-full mt-1 w-72 bg-white shadow-2xl rounded-lg border border-slate-100 max-h-96 overflow-y-auto z-10">
                    {toc.map((c) => (
                      <button
                        key={c.id}
                        onClick={() => { setPickerOpen(false); loadChapter(c.id); }}
                        className={cn(
                          "flex items-start gap-2 w-full px-3 py-2 text-left text-xs hover:bg-slate-50 border-b border-slate-50 last:border-0",
                          chapter?.id === c.id && "bg-slate-100"
                        )}
                        data-testid={`help-toc-item-${c.id}`}
                      >
                        <span className="text-slate-400 font-mono text-[10px] mt-0.5 w-6 shrink-0">{c.id.startsWith("appendix-") ? c.id.slice(-1).toUpperCase() : c.id}</span>
                        <span className="text-slate-700 font-medium">{c.title}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <button
              onClick={() => setOpen(false)}
              className="text-white/70 hover:text-white p-1"
              title="Close (Esc)"
              data-testid="help-widget-close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Body */}
          <div className="flex-1 overflow-y-auto px-5 py-4" data-testid="help-widget-body">
            {loading ? (
              <div className="flex items-center justify-center py-16 text-slate-400 text-xs">
                <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Loading chapter…
              </div>
            ) : chapter ? (
              <div className="prose prose-sm prose-slate max-w-none prose-headings:font-black prose-headings:text-slate-800 prose-a:text-blue-600 prose-code:bg-slate-100 prose-code:px-1 prose-code:py-0.5 prose-code:rounded prose-code:text-[11px] prose-code:font-normal prose-table:text-xs prose-th:bg-slate-50">
                <ReactMarkdown>{chapter.body || ""}</ReactMarkdown>
              </div>
            ) : (
              <p className="text-xs text-slate-400 text-center py-16">Open a chapter from the dropdown above.</p>
            )}
          </div>

          {/* Footer */}
          <div className="px-5 py-3 border-t border-slate-100 text-[10px] text-slate-400 flex items-center justify-between bg-slate-50">
            <span>
              Route: <code className="text-slate-500">{location.pathname}</code>
            </span>
            <a
              href="/AVIDA_USER_MANUAL.md"
              target="_blank"
              rel="noreferrer"
              className="text-slate-500 hover:text-slate-700 flex items-center gap-1 font-semibold"
              data-testid="help-widget-full-manual"
            >
              Full manual <ExternalLink className="w-2.5 h-2.5" />
            </a>
          </div>
        </div>
      )}
    </>
  );
}
